/* eslint-disable no-nested-ternary */
import React, { useMemo } from "react";

import { Grid } from "@mui/material";
import { Marker, Polyline } from "@react-google-maps/api";
import { formatInTimeZone } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import {
  ContainerPosition,
  ContainerStatus,
  Event,
  EventStatus,
  Location,
} from "../../models/marine.traffic";
import { Shipment } from "../../models/shipments";
import { formatDate } from "../../util/Format";
import { uniqueArray } from "../../util/uniqueArray";
import Button, { Color } from "../Form/Button";
import { VoyageMap, VoyageMapInformationProps } from "./Map";
import StatusProgressBar from "./ProgressBar";
import useStyles, { Text, Wrapper } from "./styles";
import { shipmentStatus } from "../../util/shipmentStatus";

interface VoyageDetailsProps {
  shipment?: Shipment;
  goBack?: () => void;
  viewLivePosition?: boolean;
  isOnViewComponent?: boolean;
  containerStatus?: ContainerStatus;
  containerPosition?: ContainerPosition;
}

export interface ProgressBarContainerEvent {
  eventTypeCode?: string;
  eventTypeDescription: string;
  eventLocationName: string;
  nextEventDateTime: string;
  iconName?: string;
  status: string;
  portId?: number;
}

const SHIP_HOME_DONE = require("../../assets/img/ship-home-done.svg");
const SHIP_HOME = require("../../assets/img/ship-home.svg");
const SHIP_MARKER_DONE = require("../../assets/img/ship-icon-done.svg");
const SHIP_MARKER = require("../../assets/img/ship-marker.svg");

/**
 * Component of Shipment Voyage & Map with a status progress steps where shows witch location the shipment it is and the estimate
 * arrival of this shipment, some information of the shipment is also showing, like status, Origin port and Destination port,
 * and container number. We also can see a map with the current position of the shipment.
 *
 * @param shipment {Shipment} object of shipment that is tracking
 * @param containerStatus {ContainerStatus} The current status of the container in the ship
 * @param containerPosition {ContainerPosition} The current position of the container in the map
 * @param isOnViewMode {Boolean} A verification if this component is showing in a view mode
 * @param viewLivePosition {Boolean} Whether this component should show a live position of the container
 * @param getBack {Function} A function to go back to the previous page
 * @returns A view with information of status and position of the container
 */
const VoyageDetails = ({
  shipment,
  goBack,
  isOnViewComponent = false,
  viewLivePosition = true,
  containerStatus,
  containerPosition,
}: VoyageDetailsProps) => {
  const { t: translate } = useTranslation();

  const classes = useStyles();

  const statusProgressBar = useMemo(() => {
    if (
      !containerStatus ||
      !containerStatus.transportationPlan ||
      !containerStatus.transportationPlan.events
    )
      return;

    const { routes, events, locations } = containerStatus.transportationPlan;
    let transportLegs = containerStatus.transportationPlan?.transportLegs;

    let departureEvent: Event | undefined;
    let loadEvent = events.find((event: Event) => event.eventTypeCode === "CGI");

    if (routes && !routes.length) {
      const originLocationPortId = locations.find(
        (location) => location.locationName === containerStatus.originPortName
      )?.portId;

      const filteredEvents = events.filter(
        (event) => event.location.portId === originLocationPortId
      );

      departureEvent = filteredEvents?.[filteredEvents.length - 1];
      loadEvent = {
        ...loadEvent,
        eventDatetime: containerStatus.originPortActualDepartureUtc,
        eventStatus: "ACT",
      } as Event;
    } else {
      const departureEventId: number = routes[0]?.departure.eventId || 0;

      departureEvent = events.find((event: Event) => event.eventId === departureEventId);
    }

    const departureLocation = locations.find(
      (location: Location) => location.portId === departureEvent?.location.portId
    );

    const transportLegsEvents: ProgressBarContainerEvent[] = [];
    const date = loadEvent?.eventDatetime ? new Date(loadEvent?.eventDatetime) : null;

    transportLegsEvents.push({
      eventTypeDescription: translate("containerGateInPort"),
      eventLocationName: departureLocation?.locationName ?? "",
      nextEventDateTime: date ? `${formatInTimeZone(date, "UTC", "dd/MM/yyyy hh:mm aa")} UTC` : "",
      iconName: "home",
      status:
        loadEvent?.eventDatetime && loadEvent?.eventStatus === "ACT"
          ? EventStatus.DONE
          : EventStatus.WAITING,
      portId: departureLocation?.portId,
    });

    if (transportLegs && !transportLegs.length) {
      const arrivalLocation = locations.find(
        (location) => location.locationName === containerStatus.finalPortName
      );

      transportLegs = [
        {
          transportLegId: "",
          departureLocation: { portId: departureEvent?.location.portId ?? 0 },
          arrivalLocation: { portId: arrivalLocation?.portId ?? 0 },
        },
      ];
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < transportLegs.length; i++) {
      const transportLeg = transportLegs[i];

      const arrivalPortId = transportLeg.arrivalLocation.portId;
      const departurePortId = transportLeg.departureLocation.portId;

      const arrivalLocation = locations.find((location) => location.portId === arrivalPortId);
      const departureLocation = locations.find((location) => location.portId === departurePortId);

      // TODO: This removed duplicated ports. Should be deleted?
      // const transportLegsEventsAlreadyHasPortId = transportLegsEvents.some(
      //   (transport) => transport.portId === arrivalLocation?.portId
      // );

      // if (transportLegsEventsAlreadyHasPortId) continue;

      if (arrivalLocation) {
        const oldStatusEvent = transportLegsEvents[i]?.status;

        const date = transportLeg.portActualArrivalUtc
          ? new Date(transportLeg.portActualArrivalUtc)
          : transportLeg.portPredictiveArrivalUtc
          ? new Date(transportLeg.portPredictiveArrivalUtc)
          : null;

        let newStatus = transportLeg.portActualArrivalUtc
          ? EventStatus.DONE
          : oldStatusEvent === EventStatus.DONE
          ? EventStatus.IN_PROGRESS
          : EventStatus.WAITING;

        if (
          !transportLeg.portActualArrivalUtc &&
          !transportLeg.portPredictiveArrivalUtc &&
          transportLeg.portActualDepartureUtc
        ) {
          const date = new Date(transportLeg.portActualDepartureUtc);
          const currentDate = new Date();

          if (date.getTime() <= currentDate.getTime()) newStatus = EventStatus.DONE;
        }

        const verifyDate =
          containerStatus.finalPortActualArrivalUtc ||
          containerStatus.finalPortPredictiveArrivalUtc ||
          containerStatus.finalPortPredictiveArrivalUtcLast ||
          null;

        const locationIsThatLastPort =
          arrivalLocation.locationName === containerStatus.finalPortName;

        if (
          !date ||
          (date && verifyDate && date.getTime() <= new Date(verifyDate).getTime()) ||
          locationIsThatLastPort
        ) {
          transportLegsEvents.push({
            eventTypeDescription: `${
              newStatus !== EventStatus.DONE
                ? date
                  ? translate("predictiveArrivalAt")
                  : translate("waitingForUpdate")
                : translate("arrivalAt")
            }`,
            eventLocationName: arrivalLocation?.locationName ?? "",
            nextEventDateTime: date
              ? `${formatInTimeZone(date, "UTC", "dd/MM/yyyy hh:mm aa")} UTC`
              : "",
            iconName: "ship",
            status: newStatus,
            portId: arrivalLocation?.portId,
          });
        }
      }

      if (departureLocation && transportLeg.portActualDepartureUtc) {
        const transport = transportLegsEvents.find(
          (legs) => legs.eventLocationName === departureLocation.locationName
        );
        const oldStatusEvent = transport?.status;
        const eventDateTime = events.find(
          (event) => event.location.portId === departurePortId
        )?.eventDatetime;

        if (oldStatusEvent !== EventStatus.DONE) {
          transportLegsEvents[i] = {
            ...transportLegsEvents[i],
            status: EventStatus.DONE,
            nextEventDateTime: transportLegsEvents[i].nextEventDateTime
              ? transportLegsEvents[i].nextEventDateTime
              : eventDateTime
              ? `${formatInTimeZone(new Date(eventDateTime), "UTC", "dd/MM/yyyy hh:mm aa")} UTC`
              : "",
          };
        }
      }
    }

    const activeStep = transportLegsEvents.findIndex(
      (transportLegEvent: ProgressBarContainerEvent) =>
        transportLegEvent.status === EventStatus.IN_PROGRESS
    );

    if (activeStep && activeStep !== -1) {
      transportLegsEvents
        .slice(activeStep + 1, transportLegsEvents.length)
        .forEach((event: ProgressBarContainerEvent) => {
          event.status = EventStatus.WAITING;
        });
    }

    return {
      events: transportLegsEvents,
      activeStep: activeStep || -1,
    };
  }, [containerStatus]);

  const location = useMemo(() => {
    const transportationPlan = containerStatus?.transportationPlan;

    if (transportationPlan) {
      const { routes, locations, events } = transportationPlan;
      const departureEventId = routes[0]?.departure?.eventId;
      const arrivalEventId = routes[0]?.arrival?.eventId;

      const departureEvent = events.find((event) => event.eventId === departureEventId);
      const arrivalEvent = events.find((event) => event.eventId === arrivalEventId);
      let departurePortId: number | null = null;
      let arrivalPortId: number | null = null;

      if (departureEvent) {
        const {
          location: { portId },
        } = departureEvent;

        if (portId) departurePortId = portId;
      }
      if (arrivalEvent) {
        const {
          location: { portId },
        } = arrivalEvent;

        if (portId) arrivalPortId = portId;
      }

      return {
        departure: locations.find((location) => location?.portId === departurePortId),
        arrival: locations.find((location) => location?.portId === arrivalPortId),
      };
    }
  }, [containerStatus]);

  const currentContainerPosition = useMemo((): VoyageMapInformationProps | undefined => {
    if (containerPosition && Object.keys(containerPosition).length > 0) {
      const { LAT, LON, TIMESTAMP, SPEED } = containerPosition;
      const latLng = { lat: Number(LAT), lng: Number(LON) };

      return {
        vesselName: containerStatus?.vesselName,
        position: latLng,
        lastUpdate: TIMESTAMP ? new Date(TIMESTAMP) : "",
        speed: SPEED ?? "",
      };
    }

    return undefined;
  }, [containerStatus, containerPosition]);

  const lastContainerPosition = useMemo((): VoyageMapInformationProps | undefined => {
    if (currentContainerPosition) {
      if (!shipment?.shipmentTracking?.nextPortName) return currentContainerPosition;

      return currentContainerPosition;
    }

    if (!containerStatus) return undefined;
    const arrivalEvents = statusProgressBar?.events;

    const { locations, transportLegs, events } = containerStatus.transportationPlan;

    const lastEventsDone = arrivalEvents?.filter((event) => event.status === EventStatus.DONE);
    const lastEventDone =
      lastEventsDone && lastEventsDone.length > 0
        ? lastEventsDone[Number(lastEventsDone?.length) - 1]
        : null;

    if (lastEventDone) {
      const { vesselName } = containerStatus;
      const nextDepartureLegAfterLastDone = transportLegs.find(
        (transportLeg) => transportLeg.departureLocation.portId === lastEventDone.portId
      );

      // to this last event, has an after departure event with date predictive or actual?
      if (
        nextDepartureLegAfterLastDone &&
        (nextDepartureLegAfterLastDone.portActualDepartureUtc ||
          nextDepartureLegAfterLastDone.portPredictiveDepartureUtc)
      ) {
        const lastLocation = locations.find(
          (location) => location.portId === nextDepartureLegAfterLastDone.departureLocation.portId
        );

        return {
          vesselName,
          position: {
            lat: Number(lastLocation?.lat),
            lng: Number(lastLocation?.lon),
          },
          lastUpdate:
            nextDepartureLegAfterLastDone.portActualDepartureUtc ||
            nextDepartureLegAfterLastDone.portPredictiveDepartureUtc,
        };
      }
      // if not has, then get location data of last event arrival done
      const lastArrivalLeg = transportLegs.find(
        (transportLeg) => transportLeg.arrivalLocation.portId === lastEventDone.portId
      );
      const lastLocation = locations.find((location) => location.portId === lastEventDone.portId);
      const loadEvent = events.find((event: Event) => event.eventTypeCode === "CGI");
      const firstDateCGI = loadEvent?.eventDatetime ? new Date(loadEvent?.eventDatetime) : null;

      return {
        vesselName,
        position: {
          lat: Number(lastLocation?.lat),
          lng: Number(lastLocation?.lon),
        },
        lastUpdate:
          lastArrivalLeg?.portActualArrivalUtc ||
          lastArrivalLeg?.portPredictiveArrivalUtc ||
          firstDateCGI ||
          "",
      };
    }
  }, [currentContainerPosition, containerStatus, statusProgressBar]);

  const allPositions = useMemo(() => {
    if (statusProgressBar?.events) {
      const locations = containerStatus?.transportationPlan?.locations;
      const { events } = statusProgressBar;

      if (locations && events) {
        const locationsPos = uniqueArray(events.map((event) => event.portId));

        const locationsPosition = [];

        for (const portId of locationsPos) {
          const location = locations.find((locationItem) => locationItem.portId === portId);

          if (location)
            locationsPosition.push({ lat: Number(location.lat), lng: Number(location.lon) });
        }

        return locationsPosition;
      }
    }

    return [];
  }, [statusProgressBar]);

  const positions = useMemo(() => {
    if (statusProgressBar?.events) {
      const locations = containerStatus?.transportationPlan?.locations;
      const { events, activeStep } = statusProgressBar;

      if (locations && events) {
        const portIdEventsConcluded = uniqueArray(
          events.slice(0, activeStep).map((event) => event.portId)
        );
        const portIdEventsInProgress = uniqueArray(
          events.slice(activeStep - 1).map((event) => event.portId)
        );

        const locationsPosition = [];
        const locationsInProgress = [];

        for (const portId of portIdEventsInProgress) {
          const location = locations.find((locationItem) => locationItem.portId === portId);

          if (location)
            locationsInProgress.push({ lat: Number(location.lat), lng: Number(location.lon) });
        }

        for (const portId of portIdEventsConcluded) {
          const location = locations.find((locationItem) => locationItem.portId === portId);

          if (location)
            locationsPosition.push({ lat: Number(location.lat), lng: Number(location.lon) });
        }

        return {
          positionsConclude: locationsPosition,
          positionsInProgress: locationsInProgress,
        };
      }
    }

    return { positionsConclude: [], positionsInProgress: [] };
  }, [statusProgressBar]);

  const getMakerFromPosition = (index: number): string => {
    if (index === 0) {
      if (
        statusProgressBar?.activeStep &&
        (statusProgressBar?.activeStep > index || statusProgressBar?.activeStep === -1)
      )
        return SHIP_HOME_DONE;

      return SHIP_HOME;
    }

    if (
      statusProgressBar?.activeStep &&
      (statusProgressBar?.activeStep > index || statusProgressBar?.activeStep === -1)
    )
      return SHIP_MARKER_DONE;

    return SHIP_MARKER;
  };

  const renderNumberOfTracking = () => {
    if (shipment) {
      if (shipment.billOfLanding?.trim().length) {
        return (
          <Grid item xs={6} md={3} lg={3} xl={3}>
            <span className={classes.subtitle}>{translate("billOfLanding")}</span>
            <p className={classes.text}>
              {shipment?.billOfLanding.toUpperCase() ?? translate("empty")}
            </p>
          </Grid>
        );
      }

      if (shipment.bookingNo?.trim().length) {
        return (
          <Grid item xs={6} md={3} lg={3} xl={3}>
            <span className={classes.subtitle}>{translate("booking")}</span>
            <p className={classes.text}>
              {shipment?.bookingNo.toUpperCase() ?? translate("empty")}
            </p>
          </Grid>
        );
      }

      if (shipment.containerNumbers?.trim().length) {
        return (
          <Grid item xs={6} md={3} lg={3} xl={3}>
            <span className={classes.subtitle}>{translate("container")}</span>
            <p className={classes.text}>
              {shipment?.containerNumbers.toUpperCase() ?? translate("empty")}
            </p>
          </Grid>
        );
      }
    }

    return (
      <Grid item xs={6} md={3} lg={3} xl={3}>
        <span className={classes.subtitle}>{translate("container")}</span>
        <p className={classes.text}>{translate("empty")}</p>
      </Grid>
    );
  };

  return (
    <div className={classes.mainDiv}>
      <div className={classes.container}>
        <Grid container direction="column">
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            item
            className={classes.infoGrid}
          >
            <StatusProgressBar
              steps={(statusProgressBar?.events as any) || []}
              activeStep={
                statusProgressBar && statusProgressBar?.activeStep !== -1
                  ? statusProgressBar?.activeStep
                  : (statusProgressBar?.events.length as any)
              }
            />
          </Grid>
          <hr className={classes.lineSeparator} />
          <Grid container direction="row" justifyContent="space-between">
            <Grid
              item
              xs={12}
              md={isOnViewComponent ? 12 : 6}
              lg={isOnViewComponent ? 12 : 6}
              container
              direction="column"
            >
              <Grid item className={classes.topSpacer}>
                <span className={classes.title}>{translate("voyageInformation")}</span>
                <Grid
                  className={classes.infoGrid}
                  spacing={3}
                  container
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                >
                  {renderNumberOfTracking()}

                  <Grid item xs={6} md={3} lg={3} xl={3}>
                    <span className={classes.subtitle}>{translate("originPort")}</span>
                    <p className={classes.text}>
                      {containerStatus?.originPortName ?? location?.departure?.locationName}
                    </p>
                  </Grid>

                  {containerStatus?.finalPortName && (
                    <Grid item xs={6} md={3} lg={3} xl={3} className={classes.finalPortNameClass}>
                      <span className={classes.subtitle}>{translate("finalDestinationPort")}</span>
                      <p className={classes.text}>{containerStatus?.finalPortName}</p>
                    </Grid>
                  )}

                  {shipment?.shipmentTracking && shipment?.shipmentTracking?.shipmentStatus && (
                    <Grid item xs={6} md={3} lg={3} xl={3}>
                      <span className={classes.subtitle}>{translate("status")}</span>
                      <p className={classes.text}>
                        {shipmentStatus(shipment?.shipmentTracking?.shipmentStatus) ??
                          translate("empty")}
                      </p>
                    </Grid>
                  )}

                  {containerStatus?.vesselName && (
                    <Grid item xs={6} md={3} lg={3} xl={3}>
                      <span className={classes.subtitle}>{translate("vesselName")}</span>
                      <p className={classes.text}>{containerStatus?.vesselName}</p>
                    </Grid>
                  )}

                  {containerStatus?.finalPortPredictiveArrivalUtc && (
                    <Grid item>
                      <span className={classes.subtitle}>{translate("shipeeziPredicts")}</span>
                      <p className={classes.text}>
                        {containerStatus?.finalPortPredictiveArrivalUtc &&
                          formatInTimeZone(
                            containerStatus?.finalPortPredictiveArrivalUtc,
                            "UTC",
                            "dd/MM/yyyy"
                          )}
                      </p>
                    </Grid>
                  )}

                  {containerStatus?.finalPortActualArrivalUtc && (
                    <Grid item>
                      <span className={classes.subtitle}>{translate("actualArrival")}</span>
                      <p className={classes.text}>
                        {containerStatus?.finalPortActualArrivalUtc &&
                          formatInTimeZone(
                            containerStatus?.finalPortActualArrivalUtc,
                            "UTC",
                            "dd/MM/yyyy"
                          )}
                      </p>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item className={classes.topSpacer}>
                {containerStatus?.finalPortCarrierEstimatedArrivalUtc && (
                  <>
                    <span className={classes.title}>{translate("travel")}</span>
                    <Grid
                      className={classes.infoGrid}
                      spacing={2}
                      container
                      direction="row"
                      justifyContent="flex-start"
                      alignItems="center"
                    >
                      <Grid
                        item
                        container
                        direction="column"
                        xs={5}
                        justifyContent="flex-start"
                        alignItems="flex-start"
                      >
                        <Grid item>
                          <span className={classes.subtitle}>
                            {translate("carrierShipeeziPredicts")}
                          </span>
                        </Grid>
                        <Grid item>
                          <p className={classes.text}>
                            {containerStatus?.finalPortCarrierEstimatedArrivalUtc &&
                              formatDate(containerStatus.finalPortCarrierEstimatedArrivalUtc)}
                          </p>
                        </Grid>
                      </Grid>
                    </Grid>
                  </>
                )}
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              md={isOnViewComponent ? 12 : 6}
              lg={isOnViewComponent ? 12 : 6}
              className={classes.topSpacer}
            >
              <Wrapper>
                <span className={classes.titleMap}>{translate("vesselPosition")}</span>
                <Text>{translate("vesselPositionUpdateDailyAt5pmUTC")}</Text>
                <Text>
                  {translate("locationIndicatedAboveIsMoreAccurateIfItDiffersFromMapDisplay")}
                </Text>
              </Wrapper>
              <VoyageMap
                information={lastContainerPosition}
                loadingMap={false}
                showLivePosition={viewLivePosition}
              >
                {!viewLivePosition ? (
                  <>
                    {allPositions.map((position, index: number) => (
                      <Marker
                        key={`position-${index}`}
                        position={position}
                        icon={{
                          url: getMakerFromPosition(index),
                          scaledSize: new google.maps.Size(30, 30),
                        }}
                      />
                    ))}
                    <Polyline
                      path={
                        statusProgressBar?.activeStep === -1 ? [] : positions.positionsInProgress
                      }
                      options={{
                        geodesic: true,
                        strokeOpacity: 0,
                        icons: [
                          {
                            icon: {
                              path: "M 0,-1 0,1",
                              strokeOpacity: 1,
                              strokeColor: " #0055A4",
                              scale: 4,
                            },
                            offset: "0",
                            repeat: "20px",
                          },
                        ],
                      }}
                    />
                    <Polyline
                      path={
                        statusProgressBar?.activeStep === -1
                          ? allPositions
                          : positions.positionsConclude
                      }
                      options={{
                        geodesic: true,
                        strokeOpacity: 1,
                        strokeColor: " #039855",
                        strokeWeight: 4,
                      }}
                    />
                  </>
                ) : null}
              </VoyageMap>
            </Grid>
          </Grid>
        </Grid>
        {goBack && (
          <Grid className={classes.cancelButton} container justifyContent="flex-end">
            <Button customColor={Color.ERROR} onClick={() => goBack && goBack()}>
              {translate("cancel")}
            </Button>
          </Grid>
        )}
      </div>
    </div>
  );
};

export default VoyageDetails;
